import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import apiService from '../services/ApiService';
import snakeToPascal from '../tools';
import { ILibraryItem, IGlobalLibraryBlock } from '../types/LibraryItem';
import {
    TYPE_META,
    getTypeLabel,
    getTypeColor,
    sortTypes,
} from './Library/typeMeta';
import { cn } from '../lib/utils';

interface LibraryBrowserModalProps {
    isOpen: boolean;
    onClose: () => void;
    parentModelDataId: number;
    scenarioId: number;
    onApplied: (newItems: { input: Record<string, any>; output: Record<string, any> }) => void;
}

type Source = 'global' | 'personal';

type BrowserRow =
    | { kind: 'personal'; item: ILibraryItem }
    | { kind: 'global'; item: IGlobalLibraryBlock };

const SOURCE_META: Record<Source, { heading: string }> = {
    global: { heading: 'Peerception blocks' },
    personal: { heading: 'My library' },
};

function rowKey(row: BrowserRow): string {
    return row.kind === 'personal' ? `p:${row.item.id}` : `g:${row.item.slug}`;
}

function rowName(row: BrowserRow): string {
    return row.item.name;
}

function rowRootType(row: BrowserRow): string {
    return row.item.root_type;
}

function rowDescription(row: BrowserRow): string | null {
    return row.item.description;
}

export default function LibraryBrowserModal({
    isOpen,
    onClose,
    parentModelDataId,
    scenarioId,
    onApplied,
}: LibraryBrowserModalProps) {
    const [globalRows, setGlobalRows] = useState<BrowserRow[]>([]);
    const [personalRows, setPersonalRows] = useState<BrowserRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [activeSource, setActiveSource] = useState<Source>('personal');
    const [activeType, setActiveType] = useState<string>('All');

    useEffect(() => {
        if (isOpen) {
            fetchItems();
            setSelectedKey(null);
            setSearch('');
            setActiveSource('personal');
            setActiveType('All');
        }
    }, [isOpen, parentModelDataId]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const [personalRes, globalRes] = await Promise.all([
                apiService.getLibraryItems(parentModelDataId),
                apiService.getGlobalLibraryBlocks(parentModelDataId),
            ]);
            const personal: ILibraryItem[] = personalRes && personalRes.ok ? await personalRes.json() : [];
            const globals: IGlobalLibraryBlock[] = globalRes && globalRes.ok ? await globalRes.json() : [];
            setGlobalRows(globals.map(item => ({ kind: 'global' as const, item })));
            setPersonalRows(personal.map(item => ({ kind: 'personal' as const, item })));
        } catch (e) {
            console.error('Failed to fetch library items', e);
        }
        setLoading(false);
    };

    const handleApply = async () => {
        const all = [...globalRows, ...personalRows];
        const selected = all.find(r => rowKey(r) === selectedKey);
        if (!selected) return;
        setApplying(true);
        try {
            const response = selected.kind === 'personal'
                ? await apiService.applyLibraryItem(selected.item.id, parentModelDataId, scenarioId)
                : await apiService.applyGlobalLibraryBlock(selected.item.slug, parentModelDataId, scenarioId);
            if (response && response.ok) {
                const data = await response.json();
                onApplied(data);
                onClose();
            }
        } catch (e) {
            console.error('Failed to apply library item', e);
        }
        setApplying(false);
    };

    const sourceRows = activeSource === 'global' ? globalRows : personalRows;
    const sourceMeta = SOURCE_META[activeSource];

    // Per-source category counts for the sidebar.
    const globalTypeCounts = useMemo(() => {
        const c: Record<string, number> = {};
        for (const r of globalRows) c[r.item.root_type] = (c[r.item.root_type] || 0) + 1;
        return c;
    }, [globalRows]);
    const personalTypeCounts = useMemo(() => {
        const c: Record<string, number> = {};
        for (const r of personalRows) c[r.item.root_type] = (c[r.item.root_type] || 0) + 1;
        return c;
    }, [personalRows]);
    const globalSidebarTypes = useMemo(() => sortTypes(Object.keys(globalTypeCounts)), [globalTypeCounts]);
    const personalSidebarTypes = useMemo(() => sortTypes(Object.keys(personalTypeCounts)), [personalTypeCounts]);

    // Auto-fall-back to "All" when the active category disappears (e.g. after
    // switching source, or when items list arrives).
    useEffect(() => {
        const allowed = activeSource === 'global' ? globalSidebarTypes : personalSidebarTypes;
        if (activeType !== 'All' && !allowed.includes(activeType)) {
            setActiveType('All');
        }
    }, [activeSource, globalSidebarTypes, personalSidebarTypes, activeType]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return sourceRows.filter(r =>
            (activeType === 'All' || r.item.root_type === activeType) &&
            (q === '' || rowName(r).toLowerCase().includes(q) || snakeToPascal(rowRootType(r)).toLowerCase().includes(q))
        );
    }, [sourceRows, activeType, search]);

    const selectSource = (source: Source) => {
        setActiveSource(source);
        setActiveType('All');
    };

    return (
        <Transition appear show={!!isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={applying ? () => {} : onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="flex items-center justify-center min-h-full p-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-[1080px] h-[80vh] bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-[10px] font-bold text-primary uppercase bg-primary/10 px-2 py-0.5 rounded tracking-widest">
                                            Library
                                        </span>
                                        <Dialog.Title className="text-[16px] font-semibold text-slate-900 truncate">
                                            Add from Library
                                        </Dialog.Title>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={applying ? undefined : onClose}
                                        disabled={applying}
                                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-40"
                                    >
                                        <span className="material-symbols-outlined text-[22px]">close</span>
                                    </button>
                                </div>

                                {/* Body: sidebar + items */}
                                <div className="flex-1 flex min-h-0">
                                    {/* Sidebar */}
                                    <aside className="w-60 shrink-0 border-r border-slate-100 bg-white py-4 px-3 flex flex-col gap-1 overflow-y-auto">
                                        <SidebarGroup
                                            source="personal"
                                            label="My library"
                                            totalCount={personalRows.length}
                                            types={personalSidebarTypes}
                                            typeCounts={personalTypeCounts}
                                            activeSource={activeSource}
                                            activeType={activeType}
                                            onSelectSource={() => selectSource('personal')}
                                            onSelectType={type => { setActiveSource('personal'); setActiveType(type); }}
                                        />
                                        <div className="h-2" />
                                        <SidebarGroup
                                            source="global"
                                            label="Peerception blocks"
                                            totalCount={globalRows.length}
                                            types={globalSidebarTypes}
                                            typeCounts={globalTypeCounts}
                                            activeSource={activeSource}
                                            activeType={activeType}
                                            onSelectSource={() => selectSource('global')}
                                            onSelectType={type => { setActiveSource('global'); setActiveType(type); }}
                                        />
                                    </aside>

                                    {/* Main */}
                                    <main className="flex-1 min-w-0 px-6 py-5 flex flex-col gap-3 overflow-hidden">
                                        {/* Section header */}
                                        <div className="flex items-end justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-[14px] font-semibold text-slate-900">
                                                        {sourceMeta.heading}
                                                    </h3>
                                                    {activeType !== 'All' && (
                                                        <>
                                                            <span className="text-slate-400">›</span>
                                                            <span
                                                                className="inline-block px-2 py-0.5 text-[11.5px] font-medium border rounded"
                                                                style={{
                                                                    color: getTypeColor(activeType),
                                                                    borderColor: getTypeColor(activeType) + '33',
                                                                    background: getTypeColor(activeType) + '0d',
                                                                }}
                                                            >
                                                                {getTypeLabel(activeType)}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Search row */}
                                        <div className="flex items-center justify-between gap-4 shrink-0">
                                            <div className="relative flex-1 max-w-[440px]">
                                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] pointer-events-none">
                                                    search
                                                </span>
                                                <input
                                                    type="text"
                                                    value={search}
                                                    onChange={e => setSearch(e.target.value)}
                                                    placeholder="Search compatible blocks..."
                                                    autoFocus
                                                    disabled={applying}
                                                    className="w-full h-10 pl-10 pr-3 border border-gray-200 rounded-lg bg-white text-[13.5px] outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
                                                />
                                            </div>
                                            <span className="text-[13px] text-slate-500 shrink-0">
                                                {filteredRows.length} {filteredRows.length === 1 ? 'item' : 'items'}
                                            </span>
                                        </div>

                                        {/* Items */}
                                        <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-[10px] overflow-hidden flex flex-col">
                                            <div className="flex items-center px-4 py-2 bg-slate-50 border-b border-gray-200 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.05em] shrink-0">
                                                <div className="w-6 shrink-0" />
                                                <div className="flex-1 min-w-0">Name</div>
                                                <div className="w-[180px]">Type</div>
                                            </div>
                                            <div className="flex-1 overflow-y-auto">
                                                {loading ? (
                                                    <div className="flex items-center justify-center py-12 text-sm text-slate-400">
                                                        Loading...
                                                    </div>
                                                ) : filteredRows.length === 0 ? (
                                                    <div className="py-12 px-4 text-center text-[13.5px] text-slate-400">
                                                        <div className="font-medium text-slate-600">No items found</div>
                                                        <div className="mt-1">
                                                            {sourceRows.length === 0
                                                                ? activeSource === 'global'
                                                                    ? 'No compatible Peerception blocks for this destination.'
                                                                    : 'No compatible blocks in your library.'
                                                                : 'Try a different search term or category.'}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    filteredRows.map(row => (
                                                        <BrowserRowItem
                                                            key={rowKey(row)}
                                                            row={row}
                                                            isSelected={selectedKey === rowKey(row)}
                                                            disabled={applying}
                                                            onSelect={() => setSelectedKey(prev => prev === rowKey(row) ? null : rowKey(row))}
                                                        />
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </main>
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
                                    <button
                                        onClick={onClose}
                                        disabled={applying}
                                        className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleApply}
                                        disabled={!selectedKey || applying}
                                        className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {applying ? (
                                            <>
                                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white" />
                                                Adding...
                                            </>
                                        ) : (
                                            'Add to model'
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

interface SidebarGroupProps {
    source: Source;
    label: string;
    totalCount: number;
    types: string[];
    typeCounts: Record<string, number>;
    activeSource: Source;
    activeType: string;
    onSelectSource: () => void;
    onSelectType: (type: string) => void;
}

function SidebarGroup({
    source,
    label,
    totalCount,
    types,
    typeCounts,
    activeSource,
    activeType,
    onSelectSource,
    onSelectType,
}: SidebarGroupProps) {
    const isActiveSource = activeSource === source;

    return (
        <div>
            <button
                type="button"
                onClick={onSelectSource}
                className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-left transition-colors text-slate-800 font-semibold hover:bg-slate-100"
            >
                <span className="text-[12px] uppercase tracking-[0.06em] truncate">{label}</span>
                <span className="text-[12px] tabular-nums shrink-0 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {totalCount}
                </span>
            </button>
            <div className="mt-0.5 ml-2 pl-2 border-l border-slate-100 flex flex-col gap-0.5">
                {types.length === 0 ? (
                    <span className="px-2 py-1.5 text-[12px] text-slate-400 italic">No items</span>
                ) : (
                    types.map(type => {
                        const isActive = isActiveSource && activeType === type;
                        const color = getTypeColor(type);
                        return (
                            <button
                                key={type}
                                type="button"
                                onClick={() => onSelectType(type)}
                                className={cn(
                                    'flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left text-[13px] transition-colors',
                                    isActive
                                        ? 'bg-slate-100 text-slate-900 font-medium'
                                        : 'text-slate-600 hover:bg-slate-50',
                                )}
                            >
                                <span className="flex items-center gap-2 min-w-0">
                                    <span
                                        className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                                        style={{ background: color }}
                                    />
                                    <span className="truncate">{getTypeLabel(type)}</span>
                                </span>
                                <span className="text-[11.5px] text-slate-400 tabular-nums shrink-0">
                                    {typeCounts[type] || 0}
                                </span>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}

interface BrowserRowItemProps {
    row: BrowserRow;
    isSelected: boolean;
    disabled: boolean;
    onSelect: () => void;
}

function BrowserRowItem({ row, isSelected, disabled, onSelect }: BrowserRowItemProps) {
    const isGlobal = row.kind === 'global';
    const color = getTypeColor(rowRootType(row));
    const label = getTypeLabel(rowRootType(row));
    const description = rowDescription(row);

    return (
        <button
            type="button"
            onClick={onSelect}
            disabled={disabled}
            className={cn(
                'w-full flex items-start px-4 py-3 border-b border-slate-100 last:border-b-0 cursor-pointer transition-colors text-left disabled:opacity-50',
                isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/60',
            )}
        >
            <div className="w-6 shrink-0 pt-0.5">
                <span className={cn(
                    'material-symbols-outlined text-[18px]',
                    isSelected ? 'text-black' : 'text-slate-300',
                )}>
                    {isSelected ? 'check_circle' : 'radio_button_unchecked'}
                </span>
            </div>
            <div className="flex-1 min-w-0 pr-4">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium text-[13.5px] text-slate-900 truncate">
                        {rowName(row)}
                    </span>
                    {isGlobal && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-medium bg-blue-50 text-blue-700 border border-blue-100 shrink-0">
                            Peerception
                        </span>
                    )}
                </div>
                {description && (
                    <p className="text-[12px] text-slate-500 mt-0.5 truncate">{description}</p>
                )}
            </div>
            <div className="w-[180px] shrink-0 pt-0.5">
                <span
                    className="inline-block px-2 py-0.5 text-[11.5px] font-medium border rounded"
                    style={{
                        color,
                        borderColor: color + '33',
                        background: color + '0d',
                    }}
                >
                    {label}
                </span>
            </div>
        </button>
    );
}
